import React, { useEffect } from 'react';
import { Content } from '../useContentData';
import { getContentType } from './registry';
import DefaultRenderer from './DefaultRenderer';
import useContentVersion from './useContentVersion';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { AlertCircle } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';

interface ContentRendererProps {
  content: Content;
  versionNumber?: number;
  showControls?: boolean;
}

/**
 * Content renderer that selects the appropriate component based on content type
 */
export const ContentRenderer: React.FC<ContentRendererProps> = ({ 
  content, 
  versionNumber,
  showControls = true
}) => {
  // Guard against null content
  if (!content) {
    return <div>No content available</div>;
  }

  // Use the version hook to get the correct version of the content
  const { versionedContent, isLoading, error, refreshVersion } = useContentVersion({ 
    content, 
    versionNumber 
  });

  // Force refresh when current_version changes
  useEffect(() => {
    // After content edits, this ensures we're displaying the latest version
    refreshVersion();
  }, [content.current_version, refreshVersion]);

  // If loading, show a skeleton
  if (isLoading) {
    return (
      <div className="p-6 h-full">
        <Skeleton className="h-12 w-3/4 mb-4" />
        <Skeleton className="h-6 w-1/2 mb-2" />
        <Skeleton className="h-32 w-full mb-6" />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-24 w-full" />
        </div>
      </div>
    );
  }

  // If error, show an error message
  if (error) {
    return (
      <div className="p-6">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error Loading Content</AlertTitle>
          <AlertDescription>{error.message}</AlertDescription>
        </Alert>
      </div>
    );
  }

  // Get the renderer for this content type from the registry
  const contentType = getContentType(content.content_type);
  
  // Add debug logging to help diagnose renderer issues
  // console.log('ContentRenderer - Content type:', content.content_type);
  // console.log('ContentRenderer - Found in registry:', !!contentType);
  // console.log('ContentRenderer - Has renderer:', !!(contentType?.renderer));
  
  // If we have a registered renderer, use it, otherwise fall back to default
  if (contentType?.renderer && versionedContent) {
    const SpecificRenderer = contentType.renderer;
    return <SpecificRenderer content={versionedContent} versionNumber={versionNumber} showControls={showControls} />;
  }
  
  // Use default renderer as fallback
  return <DefaultRenderer content={content} versionNumber={versionNumber} showControls={showControls} />;
};

export default ContentRenderer; 